import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PACKAGE_LABEL,
  STATUS_LABEL,
  type OrdersListSort,
  type OrderStatus,
  type PackageType,
} from "@/lib/admin-orders-shared";
import { cn } from "@/lib/utils";

const STATUS_PILLS: OrderStatus[] = [
  "PENDING",
  "IN_PROGRESS",
  "IN_REVIEW",
  "REVISIONS_PENDING",
  "COMPLETED",
  "CANCELLED",
];

const SORT_OPTIONS: { value: OrdersListSort; label: string }[] = [
  { value: "createdAt_desc", label: "Newest" },
  { value: "createdAt_asc", label: "Oldest" },
  { value: "deadlineAt_asc", label: "Deadline ↑" },
  { value: "deadlineAt_desc", label: "Deadline ↓" },
  { value: "priceUsd_desc", label: "Price ↓" },
  { value: "priceUsd_asc", label: "Price ↑" },
];

export function OrdersFilters({
  selectedStatuses,
  selectedPackage,
  search,
  sort,
}: {
  selectedStatuses: OrderStatus[];
  selectedPackage: PackageType | null;
  search: string;
  sort: OrdersListSort;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  function pushParams(mutator: (sp: URLSearchParams) => void) {
    const next = new URLSearchParams(params);
    mutator(next);
    next.delete("page");
    navigate(`/admin?${next.toString()}`);
  }

  function toggleStatus(s: OrderStatus) {
    pushParams((next) => {
      const current = new Set(selectedStatuses);
      if (current.has(s)) current.delete(s);
      else current.add(s);
      next.delete("status");
      for (const v of current) next.append("status", v);
    });
  }

  function setPackage(value: string) {
    pushParams((next) => {
      if (!value) next.delete("package");
      else next.set("package", value);
    });
  }

  function setSort(value: string) {
    pushParams((next) => {
      if (!value || value === "createdAt_desc") next.delete("sort");
      else next.set("sort", value);
    });
  }

  function setSearch(value: string) {
    pushParams((next) => {
      if (!value) next.delete("q");
      else next.set("q", value);
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_PILLS.map((s) => {
          const active = selectedStatuses.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              aria-pressed={active}
              className={cn(
                "inline-flex h-7 items-center rounded-full border px-3 text-[11px] font-medium uppercase tracking-[0.06em] transition-colors",
                active
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground",
              )}
            >
              {STATUS_LABEL[s]}
            </button>
          );
        })}
        {selectedStatuses.length > 0 ? (
          <Button type="button" variant="ghost" size="xs" onClick={() => pushParams((next) => { next.delete("status"); })}>
            Clear
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search order #, business, or email…"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setSearch((e.target as HTMLInputElement).value);
              }
            }}
            onBlur={(e) => {
              if (e.target.value !== search) setSearch(e.target.value);
            }}
            className="pl-8"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>

        <select
          aria-label="Package"
          value={selectedPackage ?? ""}
          onChange={(e) => setPackage(e.target.value)}
          className="h-9 rounded-md border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All packages</option>
          {(Object.keys(PACKAGE_LABEL) as PackageType[]).map((p) => (
            <option key={p} value={p}>{PACKAGE_LABEL[p]}</option>
          ))}
        </select>

        <select
          aria-label="Sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-9 rounded-md border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
